import {
  DataGrid,
  FilterToolbar,
  formatBytes,
  formatDate,
  formatNumber,
  StatusBadge
} from '@ijoin/components';
import { DbInsightsTable, useGetAdminDbInsightsTables } from '@ijoin/hooks';
import { DeleteSweep } from '@mui/icons-material';
import { IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';

import { camelCase } from 'lodash';
import { FC } from 'react';
import { useToggle } from 'usehooks-ts';

import { ImportDataPurgeDialog } from './ImportDataPurgeDialog';
import { LinkButton } from './LinkButton';
import { QueryBoundary } from './QueryBoundary';

const DDL_EXPORT_URL = '/api/v3/admin/db-insights/ddl';

export const DbTablesTab: FC = () => {
  const [isPurgeOpen, togglePurgeOpen] = useToggle();

  const tables = useGetAdminDbInsightsTables();
  const rows = tables.data?.data ?? [];
  const totalBytes = rows.reduce(
    (total, row) => total + row.dataBytes + row.indexBytes,
    0
  );
  const viewCount = rows.filter(row => row.type === 'VIEW').length;

  const onExportDdl = () => window.open(DDL_EXPORT_URL, '_blank');

  const columns: GridColDef<DbInsightsTable>[] = [
    {
      field: 'name',
      flex: 2,
      headerName: 'Name',
      renderCell: params => (
        <LinkButton
          data-component='link'
          data-testid={camelCase(params.row.name)}
          params={{ table: params.row.name }}
          to='/db-insights/tables/$table'
          variant='text'>
          {params.value}
        </LinkButton>
      )
    },
    {
      field: 'type',
      headerName: 'Type',
      renderCell: params => (
        <StatusBadge severity={params.row.type === 'VIEW' ? 'info' : 'success'}>
          {params.row.type === 'VIEW' ? 'View' : 'Table'}
        </StatusBadge>
      ),
      width: 100
    },
    {
      field: 'engine',
      headerName: 'Engine',
      width: 100
    },
    {
      align: 'right',
      field: 'columns',
      headerAlign: 'right',
      headerName: 'Columns',
      valueFormatter: value => formatNumber(value),
      width: 100
    },
    {
      align: 'right',
      field: 'rows',
      headerAlign: 'right',
      headerName: 'Rows (est.)',
      valueFormatter: value => formatNumber(value),
      width: 120
    },
    {
      align: 'right',
      field: 'dataBytes',
      headerAlign: 'right',
      headerName: 'Data',
      valueFormatter: value => formatBytes(value),
      width: 100
    },
    {
      align: 'right',
      field: 'indexBytes',
      headerAlign: 'right',
      headerName: 'Index',
      valueFormatter: value => formatBytes(value),
      width: 100
    },
    {
      field: 'collation',
      flex: 1,
      headerName: 'Collation'
    },
    {
      field: 'updatedAt',
      flex: 1,
      headerName: 'Updated',
      valueFormatter: value =>
        value ? formatDate(value, { format: 'MM/DD/YYYY h:mm:ss A' }) : ''
    }
  ];

  return (
    <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
      <Typography color='text.secondary' variant='body2'>
        {tables.data
          ? `${tables.data.database} · ${formatNumber(rows.length - viewCount)} tables · ${formatNumber(viewCount)} views · ${formatBytes(totalBytes)}`
          : 'Loading schema...'}
      </Typography>

      <DataGrid
        autoHeight={false}
        columns={columns}
        disableColumnMenu
        disableRowSelectionOnClick
        getRowId={row => row.name}
        initialState={{
          pagination: { paginationModel: { page: 0, pageSize: 25 } },
          sorting: { sortModel: [{ field: 'name', sort: 'asc' }] }
        }}
        loading={tables.isFetching}
        pageSizeOptions={[15, 25, 50, 100]}
        rows={rows}
        showToolbar
        slotProps={{
          root: {
            'data-component': 'dataGrid',
            'data-testid': 'dbInsightsTables'
          },
          toolbar: {
            actions: (
              <Tooltip title='Purge import staging data'>
                <IconButton
                  color='inherit'
                  data-testid='purgeImportData'
                  onClick={togglePurgeOpen}>
                  <DeleteSweep />
                </IconButton>
              </Tooltip>
            ),
            downloadTestId: 'exportDdl',
            onDownload: onExportDdl,
            onRefresh: tables.refetch,
            showQuickFilter: true
          }
        }}
        slots={{ toolbar: FilterToolbar }}
        sx={{ flex: 1, minHeight: 0 }}
      />

      {isPurgeOpen && (
        <QueryBoundary>
          <ImportDataPurgeDialog
            isOpen={isPurgeOpen}
            onClose={togglePurgeOpen}
          />
        </QueryBoundary>
      )}
    </Stack>
  );
};

DbTablesTab.displayName = 'DbTablesTab';
